import React, {ReactNode, SyntheticEvent, DragEvent} from 'react';
import store from '@/store/store';
import {canLinkTaskToFirmEntity, canMarkTaskPrivate} from './taskPrivacy';
import util from "@/lib/util";
import * as DateUtil from "@/lib/dateutils";
import * as TaskActions from "@/store/actions/taskActions";
import * as TemplateTaskActions from "@/store/actions/templateTaskActions";
import OptionHelper from "@/Configuration/GeneralOptions/OptionHelper";
import playSound from '@/lib/audio';
import {dialogNoButtons, dialogWithCancel} from '@/ui/Confirm';
import TaskItem from './TaskItem';
import LeadHelper from '@/CRM/Sales/LeadHelper';
import * as CampaignHelper from '@/CRM/Marketing/Campaigns/CampaignHelper';
import CaseHelper from '@/Case/CaseHelper';
import IconButton from '@/ui/IconButton/IconButton';
import Icons from '@/ui/IconButton/Icons';
import InvoiceHelper from '@/Financial/Invoices/InvoiceHelper';
import {deleteImpactTask, deleteTask} from '@/store/deps/taskDeps';
import {canCascadeDelete} from '@/store/deps/deleteContext';
import type {DeleteResult} from '@/store/deps/deleteContext';
import {confirmCascadeDelete, isBlocker, reportDeleteResult} from '@/lib/cascadeDelete';
import {abandonedRecordTraversal, deleteAbandonedTask} from './abandonedTasks';
import User from '@/entities/user';
import Group from '@/entities/group';
import TemplateTask from '@/entities/templateTask';
import Alert from '@/lib/alert';
import locals from '@/lib/localStorage';
import OutlawCalendar from "@/ui/Calendar/OutlawCalendar";
import {formatFMLFromId, formatLFM, formatUniqueName, formatUniqueNameFromId, formatUserName, getUserInitials, replaceNames} from "@/Contacts/ContactFormat";
import find from "@/store/find";
import TextField from "@/ui/TextField";
import {getContactFromUser} from "@/lib/picos/getContactFromUser";
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import {roundDurationToMinutes} from "@/lib/picos/duration";
import {getTaskTime} from "@/lib/picos/taskTime";
import {isAdmin} from "@/Auth/roles";
import {getZeroUUID} from "@/lib/picos/uuids";
import newHRTimestamp from "@/lib/picos/timestamp";
import {isAfter, isToday, isTomorrow, setHours, setMinutes, setSeconds} from "date-fns";
import FinancialHelper from "@/Financial/FinancialHelper";
import {stopTimer} from "./timers";
import {toTwoDecimals} from "@/lib/picos/numberFormat";
import {dialogStore} from "@/store/zstore";
import {DialogType} from "@/store/dialogs";
import Invoice from "@/entities/invoice";
import Task, {AssignedToType, ImpactType, UrgencyType} from "@/entities/task";
import ActivityCategory from "@/entities/activityCategory";
import Category from "@/entities/category";
import ActivityType from "@/entities/activityType";
import Contact from "@/entities/contact";
import {uuid} from "@/@types/entity";
import Moment from 'moment';
import {ContextMenuItemProps} from "@/ui/ContextMenu/ContextMenuItem";
import Matter from "@/entities/matter";
import Lead from "@/entities/lead";
import {FilterFnWithArgs} from "@/@types/functions";
import {isNextUnit, isNextWeek} from "@/lib/dateutils";
import {componentCacheStore} from "@/hooks/useComponentCache";
import {canReassignTasks, guardTaskReassignment} from './taskTimerState';

export const TOP_LEVEL_ID = '00000000-0000-0000-0000-000000000000';

export const Labels: string[] = ['HH', 'MH', 'HM', 'LH', 'MM', 'HL', 'LM', 'ML', 'LL'] as const;
export type IULabel = typeof Labels[keyof typeof Labels];

export type TaskLike = Task | TemplateTask;

export enum ColorMap {
	HHM = '#efd1c8',
	HMM = '#dbdbc3',
	HLM = '#cee6d7',
	MHM = '#e6d5c5',
	MMM = '#d6ecd4',
	MLM = '#d6ecd4',
	LHM = '#d6e6ca',
	LMM = '#c6dcdc',
	LLM = '#c4cfee'
}

export const COLOR_MAP = {
	'HHM': ColorMap.HHM,
	'HMM': ColorMap.HMM,
	'HLM': ColorMap.HLM,
	'MHM': ColorMap.MHM,
	'MMM': ColorMap.MMM,
	'MLM': ColorMap.MLM,
	'LHM': ColorMap.LHM,
	'LMM': ColorMap.LMM,
	'LLM': ColorMap.LLM
}

export enum ImpactUrgencyNumber {
	HH = 1,
	MH = 2,
	HM = 3,
	LH = 4,
	MM = 5,
	HL = 6,
	LM = 7,
	ML = 8,
	LL = 9
}

function formatDescription(code: string): string {
	const impact = code[0];
	const urgency = code[1];

	function getLabel(i: string) {
		if (i === 'H') return 'High';
		if (i === 'M') return 'Medium';
		if (i === 'L') return 'Low';
	}

	return `${impact} ${getLabel(impact)}, ${urgency} ${getLabel(urgency)}`;
}

export const IMPACT_URGENCY_MAP: { num: ImpactUrgencyNumber, code: IULabel, description: string }[] = [
	{num: ImpactUrgencyNumber.HH, code: 'HH', description: formatDescription('HH')},
	{num: ImpactUrgencyNumber.MH, code: 'MH', description: formatDescription('MH')},
	{num: ImpactUrgencyNumber.HM, code: 'HM', description: formatDescription('HM')},
	{num: ImpactUrgencyNumber.LH, code: 'LH', description: formatDescription('LH')},
	{num: ImpactUrgencyNumber.MM, code: 'MM', description: formatDescription('MM')},
	{num: ImpactUrgencyNumber.HL, code: 'HL', description: formatDescription('HL')},
	{num: ImpactUrgencyNumber.LM, code: 'LM', description: formatDescription('LM')},
	{num: ImpactUrgencyNumber.ML, code: 'ML', description: formatDescription('ML')},
	{num: ImpactUrgencyNumber.LL, code: 'LL', description: formatDescription('LL')},
];

export const AWAITING_TITLE = '[Awaiting Title]';

/**
 * Get a 1-9 value for a task's impact and urgency.
 *
 * Returns undefined when the task has no impact/urgency set: a Task defaults
 * both to null, so the lookup key (e.g. "nullnull") has no matching entry in
 * IMPACT_URGENCY_MAP. Callers must tolerate undefined — do NOT assert non-null
 * here, it crashes on every unset-priority task (OWA-587).
 *
 * @param {Task} task
 * @returns {number|undefined}
 */
export const getGridNumber = (task: Task): number | undefined => {
	const v = `${task.impact}${task.urgency}`;
	return IMPACT_URGENCY_MAP.find(iu => iu.code === v)?.num;
};

/**
 * Get a description, e.g. Low Impact, High Urgency.
 *
 * Returns undefined when impact/urgency are unset (no matching map entry);
 * callers must tolerate undefined (OWA-587).
 *
 * @param {Impact} impact
 * @param {Urgency} urgency
 * @returns {string|undefined}
 */
export const getDescription = (impact: ImpactType, urgency: UrgencyType): string | undefined => {
	const v = `${impact}${urgency}`;
	return IMPACT_URGENCY_MAP.find(iu => iu.code === v)?.description;
}

/**
 * Translate a code into a grid number.
 *
 * Returns undefined when the code has no matching map entry (OWA-587).
 *
 * @param {string} code
 * @returns {number|undefined}
 */
export const getGridNumberFromCode = (code: string): ImpactUrgencyNumber | undefined => {
	return IMPACT_URGENCY_MAP.find(iu => iu.code === code)?.num;
}

/**
 * Reverse lookup of impact/urgency from grid number.
 *
 * @param {number} num
 * @returns {{urgency: string, impact: string}}
 */
export const getCodeFromGridNumber = (num: number): { urgency: UrgencyType; impact: ImpactType; } => {
	const code = IMPACT_URGENCY_MAP.find(iu => iu.num === num)!.code;
	return {
		impact: code[0],
		urgency: code[1]
	}
}

/**
 * Get the css className from a task
 * @param {Task} task
 * @returns {string}
 */
export const getTaskStyle = (task: Task): string => {
	return `${task.impact}${task.urgency}M`;
}

export const getTaskBackgroundColor = (task: Task): ColorMap => {
	return COLOR_MAP[getTaskStyle(task)];
}

/**
 * Get the css className from a grid number
 * @param {ImpactUrgencyNumber} num
 * @returns {string}
 */
export const getTaskStyleFromGridNumber = (num: ImpactUrgencyNumber): string => {
	const {impact, urgency} = getCodeFromGridNumber(num);
	return `${impact}${urgency}M`;
}

/**
 * Does the user's assignment let them run this task's timer? A user can run a task assigned to
 * them, or to a group they belong to, and an unassigned task, which the start claims. An admin can
 * run another user's task (John, 2026-10-25, TSK-Q6). This ignores whether the task is finalized,
 * so a timer that is still running on a completed task can be stopped by the person who owns it.
 *
 * @param {Task} task
 * @param {uuid} [userId] - the user who would run it, default the signed-in user
 * @returns {boolean}
 */
export const canRunTimer = (task: Task, userId: string = getCurrentUserId()): boolean => {
	// Is the task owned by a user, and are we not the user? An unassigned task is claimed by whoever
	// starts it. The nil UUID is not treated as unassigned here: only an admin may run such a task,
	// as before OWA-1648 (startTimer still claims it for the admin, and the server's assignee lock
	// accepts that claim). Only an admin may run another user's task.
	const unassigned = !task.assignedTo;
	if (task.assignedToType === 'U' && !unassigned && task.assignedTo !== userId) return isAdmin(userId);

	// Is the task assigned to a group, and we are not in that group? An admin may still run it.
	if (task.assignedToType === 'G') {
		const group = store.getState().groups.get(task.assignedTo ?? '');
		// Make sure we are not loading, can ask again when fully loaded
		if (store.getState().misc.isLoading) return false;
		else if (!group) {
			console.warn('No such group');
			return isAdmin(userId); // Can't be in a group that doesn't exist
		}
		else if (!group.userIds.includes(userId)) return isAdmin(userId);
	}

	return true;
}

/**
 * For a given task and user, can this task be started? This is the one start rule every timer
 * button and startTimer follow (OWA-1648). An invoiced or completed task never can; otherwise
 * canRunTimer decides.
 *
 * @param {Task} task
 * @param {uuid} [userId] - the user who would start it, default the signed-in user
 * @returns {boolean}
 */
export const canPlay = (task: Task, userId: string = getCurrentUserId()): boolean => {
	// Finalized tasks can not be started or stopped
	if (task.invoiceId || task.completionDate) return false;

	return canRunTimer(task, userId);
}

/**
 * Get the names of clients related to this task in any way.
 *
 * @param {Task} task
 * @returns {string} The names concatenated with an ampersand
 */
export const getClientNames = (task: Task): string => {
	// Find clients related to this task's leads (if one is connected)
	const leadClients = task.tieIns
		.filter(ti => ti.module === 'Sales' && ti.entity === 'Lead')
		.map(ti => {
			const lead = LeadHelper.getLeadFromId(ti.id);
			if (!lead) return [''];
			else
				return lead.clients.map(clientId => {
					return formatUniqueNameFromId(clientId, 'LFM');
				});
		})
		.flat() || [];

	// Find clients related to this tasks' matter (if one is connected)
	const matterClients = task.tieIns
		.filter(ti => ti.module === 'Client' && ti.entity === 'Matter')
		.map(ti => {
			const matter = CaseHelper.getMatterFromId(ti.id);
			if (!matter) return [''];
			else
				return matter.clients.map((clientId: uuid) => {
					return formatUniqueNameFromId(clientId, 'LFM');
				});
		})
		.flat() || [];

	// Combine them
	return matterClients.concat(leadClients).join(' & ');
}

/**
 * Super-basic costing method.
 *
 * @param {Task} task
 * @returns {number}
 */
function simpleCost(task: Task): number {
	const time = task.isLaborTimeOverridden ? task.laborTimeOverride : task.elapsedTime;
	return util.toTwoDecimals(task.associatedRate * getRoundedHours(null, time));
}

/**
 * Get the latest computed cost of this task.
 *
 * @param {Task} task
 * @param {boolean} [useHourlyForFlatFee] - should we assign hourly values to flat fee work?
 * @param {'simple' | 'complex'} [method] - simple method (rate * time) or complex (traditional billable)
 * @return {number}
 */
export const getUpdatedCost = (task: Task, useHourlyForFlatFee: boolean = false, method: 'simple' | 'complex' = 'complex'): number => {
	if (method === 'simple') {
		if (useHourlyForFlatFee) return simpleCost(task);
		else return task.flatFeeBucket || task.flatFeeMilestoneBucket ? 0 : simpleCost(task)
	}
	else {
		if (task.isCostOverridden) return task.costOverride;

		const taskClone = updateCost(task);
		if ((task.flatFeeMilestoneBucket || task.flatFeeBucket) && useHourlyForFlatFee) {
			const time = task.isLaborTimeOverridden ? task.laborTimeOverride : task.elapsedTime;
			return util.toTwoDecimals((task.laborRate ?? 0) * getRoundedHours(null, time));
		}
		else return taskClone.isCostOverridden ? util.toTwoDecimals(task.costOverride) : util.toTwoDecimals(taskClone.cost);
	}
};

/**
 * If a related tie in is found, either delete the task (if only one tieIn), or save the task with the tieIn removed).
 * If no related tasks with the given module and entity, check in the relatedTo field to see if it matches the id.
 * @param {string} module
 * @param {string} entity
 * @param {uuid} relatedId
 *
 * Callers run this when a new Case, lead, contact or campaign is cancelled, so the tasks deleted
 * belong to a record that was never kept. A whole task goes through `deleteAbandonedTask`, which
 * also deletes its comments and documents (OWA-930); a refusal is reported to the user.
 *
 * @returns {DeleteResult[]} one result per task deleted
 */
export const deleteRelatedTasks = (module: string, entity: string, relatedId: uuid): DeleteResult[] => {
	const results: DeleteResult[] = [];
	const traversal = abandonedRecordTraversal(relatedId);
	const matches = (ti: {module: string, entity: string, id: uuid}) => ti.module === module && ti.entity === entity && ti.id === relatedId;

	store.getState().tasks.forEach(t => {
		if (t.status === 'D') return;

		if (!module || !entity || t.tieIns.length === 0) {
			// No tieIns, just check for relatedTo entries and delete
			if (t.relatedTo.includes(relatedId)) results.push(deleteAbandonedTask(t, traversal));
		}
		else if (t.tieIns.length === 1) {
			// If only one tieIn and it matches, delete the whole task
			if (matches(t.tieIns[0])) results.push(deleteAbandonedTask(t, traversal));
		}
		else {
			// If more than one tieIn, check for this tieIn and remove the tieIn/save the task.
			// Remove from relatedTo as well. The stored task is never changed in place.
			const index = t.tieIns.findIndex(matches);
			if (index >= 0) {
				const taskClone = t.clone();
				taskClone.relatedTo = t.relatedTo.filter(r => r !== relatedId);
				taskClone.tieIns = t.tieIns.filter((_, i) => i !== index);
				if (!taskClone.title) taskClone.title = AWAITING_TITLE;
				store.dispatch(TaskActions.saveTaskQueue(taskClone));
			}
		}
	});

	const refused = results.find(result => result.status !== 'deleted');
	if (refused) reportDeleteResult('task', refused);
	return results;
};

/**
 * Get a text description of related campaigns, leads, matters, etc.
 *
 * @param {Task} task
 * @param {boolean} textOnly
 * @return {(typeof IconButton)[] | string}
 */
export const getRelated = (task: Task, textOnly: boolean): ReactNode[] | string => {
	if (!task) return '';

	const getNames = (cids: uuid[]) => cids?.map(cid => formatFMLFromId(cid)).join(', ') ?? '';

	let i = 1;
	const getId = (type: string) => {
		return `related-${type}-${task.id}-${i++}`;
	}

	const relatedCampaigns = task.tieIns
		.filter(ti => (ti.module === 'Marketing') && (ti.entity === 'Campaign'))
		.map(ti => CampaignHelper.getCampaignFromId(ti.id))
		.filter(util.existsFilter)
		.map(campaign => {
			if (textOnly) return campaign.name;
			else return <IconButton key={`related-campaign-${campaign.id}`}
									id={getId('campaign')}
									type={Icons.MARKETING}
									label={campaign.name}
			/>
		});

	const relatedLeads = task.tieIns
		.filter(ti => (ti.module === 'Sales') && (ti.entity === 'Lead'))
		.map(ti => LeadHelper.getLeadFromId(ti.id))
		.filter(util.existsFilter)
		.map(lead => {
			if (textOnly) return `${lead.name} (${getNames(lead.clients)})`;
			else return <IconButton key={`related-lead-${lead.id}`}
									id={getId('lead')}
									type={Icons.LEAD}
									label={`${lead.name} (${getNames(lead.clients)})`}
			/>
		});

	const relatedMatters = task.tieIns
		.filter(ti => (ti.module === 'Client') && (ti.entity === 'Matter'))
		.map(ti => CaseHelper.getMatterFromId(ti.id))
		.filter(util.existsFilter)
		.map(matter => {
			if (textOnly) return `${matter.name} (${getNames(matter.clients)})`;
			else return <IconButton key={`related-matter-${matter.id}`}
									id={getId('matter')}
									type={Icons.CASE}
									label={`${matter.name} (${getNames(matter.clients)})`}
			/>
		});

	const relatedContacts = !relatedCampaigns && !relatedLeads && !relatedMatters && task.relatedTo
							? task.relatedTo
								.map(cid => find<Contact>('contacts', cid))
								.filter(util.existsFilter)
								.map(contact => {
									if (textOnly) return formatLFM(contact, false);
									else return <IconButton key={`related-contact-${contact.id}`}
															id={getId('contact')}
															type={Icons.CONTACT}
															label={formatLFM(contact, false)}
									/>
								})
							: null;

	// Return an array of related relationships
	const result = relatedCampaigns
		.concat(relatedLeads)
		.concat(relatedMatters)
		.filter(util.existsFilter) ?? relatedContacts;

	if (textOnly) return result.flat().join(' ');
	else return result;
};

/**
 * Get leads related to a task.
 *
 * @param {Task} task
 * @returns {Lead[]}
 */
export const getRelatedLeads = (task: Task): Lead[] => {
	return task.tieIns
		.filter(ti => (ti.module === 'Sales') && (ti.entity === 'Lead'))
		.map(ti => store.getState().leads.get(ti.id))
		.filter(util.existsFilter);
};

/**
 * Get matters related to a task.
 *
 * @param {Task} task
 * @returns {Matter[]}
 */
export const getRelatedMatters = (task: Task): Matter[] => {
	return task.tieIns
		.filter(ti => (ti.module === 'Client') && (ti.entity === 'Matter'))
		.map(ti => store.getState().matters.get(ti.id))
		.filter(util.existsFilter);
};

/**
 * The rounded times and effective rates behind a task's hourly cost. getCost and updateCost both
 * read it, so the editor, the timer stop, invoices and flat fee labor share one set of numbers.
 * The billing increment and rounding method come from the invoice, then the firm options, with
 * no built-in default. A rate is the override when overridden, else the task's own rate, else the
 * associated rate (OWA-1645).
 *
 * @param {Task} task
 * @param {Invoice} [invoice]
 */
export const getCostParts = (task: Task, invoice?: Invoice | null): {
	elapsedTime: number;
	travelTime: number;
	laborTime: number;
	laborRate: number;
	travelRate: number;
} => {
	if (!invoice) invoice = task.invoiceId ? InvoiceHelper.getInvoiceFromId(task.invoiceId) : null;

	const billingIncrement = invoice?.billingIncrement ?? new OptionHelper().getOptionValue(OptionHelper.BILLING_INCREMENT) as number;
	const roundingMethod = invoice?.roundingMethod ?? new OptionHelper().getOptionValue(OptionHelper.ROUNDING_METHOD) as string;

	// Ensure that the travelTime and laborTime add up to the elapsedTime
	const elapsedTimeArg = task.elapsedTime + (task.running ? Date.now() - task.lastStart : 0);
	const elapsedTime = roundDurationToMinutes(elapsedTimeArg, billingIncrement, roundingMethod);

	// Travel time can not be larger than elapsed time
	let travelTime = 0;
	if (task.travelTime || task.isTravelTimeOverridden) {
		const travelTimeArg = Math.min(elapsedTime, task.isTravelTimeOverridden ? task.travelTimeOverride : task.travelTime);
		travelTime = roundDurationToMinutes(travelTimeArg, billingIncrement, roundingMethod);
	}

	// Labor time is whatever is left after travel time
	const laborTimeArg = task.isLaborTimeOverridden ? task.laborTimeOverride : (elapsedTime - travelTime);
	const laborTime = roundDurationToMinutes(laborTimeArg, billingIncrement, roundingMethod);

	const laborRate = task.isLaborRateOverridden
					  ? task.laborRateOverride || 0
					  : task.laborRate || task.associatedRate || 0;

	const travelRate = task.isTravelRateOverridden
					   ? task.travelRateOverride || 0
					   : task.travelRate || task.associatedRate || 0;

	return {elapsedTime, travelTime, laborTime, laborRate, travelRate};
};

/**
 * Update the activity's labor time, travel time and cost based on all the parameters. The stored
 * cost is always getCost(task), the one formula the editor, invoices and reports use (OWA-1645).
 *
 * @param {Task} task - task to compute (immutable, always returns clone)
 * @param {object} [toUpdate] - object with {fieldName: value} pairs
 * @return {Task}
 */
export const updateCost = (task: Task, toUpdate?: object): Task => {
	let taskClone: Task;
	let hasCloned = false;

	function clone(current: Task) {
		if (!hasCloned) {
			hasCloned = true;
			return current.clone();
		}
		else return current;
	}

	if (toUpdate) {
		// Check each of the update fields for parity, if any is different, we update with a clone.
		const diffs = Object.keys(toUpdate).some(key => {
			return task[key] !== toUpdate[key];
		});

		if (diffs) {
			taskClone = Object.assign(clone(task), toUpdate);
		}
		else taskClone = task;
	}
	else taskClone = task;

	const wasPersisted = !!taskClone.persisted;

	// Determine the total cost, if billable
	if (taskClone.billable && taskClone.status !== 'D') {
		const invoice = taskClone.invoiceId ? find<Invoice>('invoices', taskClone.invoiceId) : null;
		const {travelTime, laborTime} = getCostParts(taskClone, invoice);

		// Set the travel and labor time on the activity
		if (task.travelTime !== travelTime) taskClone.travelTime = travelTime;
		if (task.laborTime !== laborTime) taskClone.laborTime = laborTime;

		const cost = getCost(taskClone, invoice);
		if (task.cost !== cost) taskClone.cost = cost;
	}

	if (hasCloned) {
		if (taskClone.persisted !== wasPersisted) taskClone.persisted = wasPersisted;
	}

	return taskClone;
};

/**
 * Get the cost for this task, taking into account all the complexities. This is the only task
 * cost formula: the editor and the timer stop store it, and invoices, PDFs, final values and
 * reports bill it.
 *
 * @param {Task} task
 * @param {Invoice} [invoice]
 * @returns {number}
 */
export const getCost = (task: Task, invoice?: Invoice | null): number => {
	// Was the cost overridden, a flat fee or a no charge?
	if (task.noCharge || task.hideFromInvoice) return 0;

	if (task.billable) {
		if (task.isCostOverridden) return toTwoDecimals(task.costOverride);

		if (task.flatFeeBucket) {
			// Make sure the bucket exists
			return FinancialHelper.getFlatFeePortion(task);
		}
		else {
			const {laborRate, laborTime, travelRate, travelTime} = getCostParts(task, invoice);

			// Calculate the total cost. Don't forget to use fractional hours if requested in
			return toTwoDecimals(((laborRate || 0) * (laborTime / 3600000 || 0)) + ((travelRate || 0) * (travelTime / 3600000 || 0)));
		}
	}
	else return 0;
}

/**
 * What a task earns at its hourly rates, as if it were not part of a flat fee. The flat fee labor
 * column shows this next to the fee, so it runs the same getCost formula (OWA-1593).
 *
 * @param {Task} task
 * @param {Invoice} [invoice]
 * @returns {number}
 */
export const getHourlyCost = (task: Task, invoice?: Invoice | null): number => {
	if (!task.flatFeeBucket) return getCost(task, invoice);

	const hourly = task.clone();
	hourly.flatFeeBucket = null;
	return getCost(hourly, invoice);
}

/**
 * Determine the base cost of this task, regardless of overrides, discounts and flat fees.
 *
 * @param {Task} task
 * @returns {number}
 */
export const getBaseCost = (task: Task): number => {
	if (task.billable && !task.running && task.status !== 'D') {
		const invoice = task.invoiceId ? InvoiceHelper.getInvoiceFromId(task.invoiceId) : null;

		const billingIncrement = invoice?.billingIncrement ?? OptionHelper.getOptionValueStatic(OptionHelper.BILLING_INCREMENT) as number;
		const roundingMethod = invoice?.roundingMethod ?? OptionHelper.getOptionValueStatic(OptionHelper.ROUNDING_METHOD) as string;

		// Honour the user's manual labor-time override if set; otherwise use elapsedTime
		const elapsedTimeArg = task.isLaborTimeOverridden ? task.laborTimeOverride : task.elapsedTime;
		const laborTime = roundDurationToMinutes(elapsedTimeArg, billingIncrement, roundingMethod);

		return (task.laborRate || 0) * ((laborTime / 3600000) || 0);
	}
	else return 0;
}

/**
 * Quickly get the task's labor time (in hours) as reported on the invoice.
 *
 * @param {Invoice} invoice
 * @param {number} elapsedTime
 */
export const getRoundedHours = (invoice: Invoice | null = null, elapsedTime: number): number => {
	const billingIncrement = invoice?.billingIncrement ?? OptionHelper.getOptionValueStatic(OptionHelper.BILLING_INCREMENT) as number;
	const roundingMethod = invoice?.roundingMethod ?? OptionHelper.getOptionValueStatic(OptionHelper.ROUNDING_METHOD) as string;

	// How many significant digits do we need?
	let digits = (billingIncrement / 60).toString().length - 2;
	if (digits > 2) digits = 2;

	return parseFloat((roundDurationToMinutes(elapsedTime, billingIncrement, roundingMethod) / 3600000.0).toFixed(digits));
}

/**
 * Get the elapsed time from a task, taking into consideration overridden time.
 * Do NOT use this call for a timer, as we only care about truly elapsed time there!
 *
 * @param {Task} task
 * @returns {number}
 */
export const getElapsedTime = (task: Task): number => {
	if (!task) throw new Error('task is null');

	return getTaskTime(task);
}

export function getTaskNumber(task: Task): ImpactUrgencyNumber {
	return Labels.indexOf(`${task.urgency}${task.impact}`) + 1 as ImpactUrgencyNumber;
}

export function sortTasksByPriority(a: Task | null | undefined, b: Task | null | undefined) {
	if (!a) return 1;
	if (!b) return -1;

	const na = getTaskNumber(a);
	const nb = getTaskNumber(b);

	return nb - na;
}

export function sortTasksByPriorityReverse(a: Task, b: Task): number {
	const na = getTaskNumber(a);
	const nb = getTaskNumber(b);

	const naRunning = a.running ? 1 : 0;
	const nbRunning = b.running ? 1 : 0;

	if (naRunning || nbRunning) return naRunning - nbRunning;

	if (na !== nb) return na - nb;
	else return ((b.dueDate?.getTime() || 0) - (a.dueDate?.getTime() || 0));
}

/**
 * Sort tasks by date, descending.
 *
 * @param {Task} a
 * @param {Task} b
 * @returns {number}
 */
export function sortTasksByReverseDate(a: Task, b: Task): number {
	return (b.completionDate?.getTime() || b.dueDate?.getTime() || 0) - ((a.completionDate?.getTime() || 0) - (a.dueDate?.getTime() || 0));
}

/**
 * Handle the complete action of checking or unchecking a task.
 * - Do nothing if part of a finalized invoice
 * - If running, stop it first
 * - If not completed yet, set the completion date
 * - If already completed, remove the completion date
 *
 * @param {Task} task
 * @returns {Task} - modified task
 */
export function handleCheckboxClick(task: Task): Task {
	// Don't do anything if this is part of a finalized invoice
	if (!FinancialHelper.getFinalizedByInvoiceId(task.invoiceId ?? '')) {

		// Should we ring the bell?
		const useSound = OptionHelper.getOptionValueStatic(OptionHelper.SOUND_FOR_TASK_COMPLETE);
		if (useSound) playSound('bell');

		// Always ensure we are dealing with a cloned object
		let taskClone = task.clone();

		// Stop the task, if it is running
		if (taskClone.running) taskClone = stopTimer(taskClone, getCurrentUserId(), true);

		// Set completion date if checking, remove if unchecking
		if (taskClone.completionDate === null) taskClone.completionDate = new Date();
		else taskClone.completionDate = null;

		taskClone.addedOn = newHRTimestamp();
		taskClone.addedBy = getCurrentUserId();
		taskClone.status = 'U';

		// Always ensure there is a title
		if (!taskClone.title) taskClone.title = AWAITING_TITLE;

		// Save the changes
		store.dispatch(TaskActions.saveTaskQueue(taskClone));

		return taskClone;
	}
	else return task;
}

/**
 * Show a popup editor for this task.
 *
 * @param {Task} task
 * @param {Partial<Task>} [presets]
 * @param {Function} [onOk]
 */
export function editTask(task: Task, presets?: Partial<Task>, onOk?: Function) {
	const handleTask = (task: Task) => {
		if (!task.title) task.title = AWAITING_TITLE;
		store.dispatch(TaskActions.saveTaskQueue(task));

		if (onOk) onOk(task);
	};

	const cancelOut = () => {
		// Clear out autosave for this item
		window.storageWorker
			.uncacheItem(getCurrentFirmId(), getCurrentUserId(), 'task', getZeroUUID(), task.id)
			.catch(err => console.error(err));
	};

	dialogStore.actions.openTypedDialog(DialogType.Task, {
		title: 'Edit Task',
		dialogType: DialogType.Task,
		componentProps: {
			current: task,
			embedded: true,
			presets,
			noCloseWarning: false,
			fitWidth: true,
		},
		onOk: handleTask,
		onCancel: cancelOut,
	});
}

/**
 * Compare two tasks for sorting
 * @param {Task} a
 * @param {Task} b
 */
export function compareTasks(a: Task, b: Task): number {
	// Was a.running ? -1 : b.running? 1 : (a.dueDate < b.dueDate) ? -1 : (a.dueDate > b.dueDate) ? 1 : 0

	// Is a notification?
	if ((a.notification?.status === 'r') && a.notification?.to === getCurrentUserId()) return -1;
	else if ((b.notification?.status === 'r') && b.notification?.to === getCurrentUserId()) return 1;

	// If a is running, it goes first
	else if (a.running) return -1;

	// If b is running, it goes first (yes, it looks like that doesn't make sense, but it does)
	else if (b.running) return 1;

	// Otherwise sort by rank, then due date
	else {
		const ga = getGridNumber(a) ?? 9;
		const gb = getGridNumber(b) ?? 9;
		if (ga === gb) return (b.completionDate?.getTime() || b.dueDate?.getTime() || 0) - (a.completionDate?.getTime() || a.dueDate?.getTime() || 0);
		else return ga - gb;
	}
}

/**
 * Get a task by its ID.
 *
 * @param {uuid} taskId
 * @returns {Task}
 */
export function getTaskById(taskId: uuid): Task | null {
	return store.getState().tasks.get(taskId);
}

/**
 * Start a drag event.  We set some types and data on dataTransfer object, so that we can return sensible
 * information on the other events.
 *
 * Specifically, we set this data:
 *
 * application/{entityName} is set to the ID of the source
 * dnd/{sourceId} is set to an the ID of the source
 *
 * We then call the parent's onDragStart.
 *
 * @param {DragEvent} e
 * @param {Task} source
 */
export function onDragStart(e: React.DragEvent, source: Task) {
	e.dataTransfer.setData('text', source.id);

	const words = source.title?.split(' ');
	let text = words?.slice(0, 8).join(' ') || '';
	if (source.title?.length > text.length) text += '...';

	const span = document.createElement('span');
	const textNode = document.createTextNode(text);
	span.appendChild(textNode);
	span.id = 'dropSpan';
	span.style.position = "absolute";
	span.style.top = "-1000px";
	span.style.backgroundColor = 'white';
	span.textContent = text;
	document.body.append(span);

	e.dataTransfer.setDragImage(span, 0, 0);
}

/**
 * Release the drop image we created.
 */
export function onDragEnd() {
	const dropImage = document.getElementById('dropSpan');
	if (dropImage) document.body.removeChild(dropImage);
}

/**
 * Get tie-ins for a task
 * @param {Task} task
 * @param {boolean} idsOnly
 */
export function getMatterTieIns(task: Task, idsOnly: boolean) {
	const matters = store.getState().matters;

	return task.tieIns
		.filter(ti => ti.module === 'Client' && ti.entity === 'Matter')
		.filter(ti => matters.get(ti.id))
		.map(ti => {
			// Get the matching matter
			const matter = matters.get(ti.id)!;

			if (idsOnly) return matter.id;
			else return {
				label: window.__demo_mode__
					   ? replaceNames(store.getState().contacts
						.filter(c => c.status !== 'D')
						.map(c => c.id), matter.name)
					   : matter.name,
				value: matter.name,
				matter
			};
		});
}

/**
 * Determine the rate for this task, based on user, default rates and case rates.
 * @param {uuid} userId
 * @param {Matter} matter
 * @return {number}
 */
export function getBillableRate(userId: uuid, matter: Matter): number {
	// Who is the user?
	const user = store.getState().users.get(userId) ||
		store.getState().groups.get(userId);

	const rates = matter
				  ? [matter]
					  .filter(m => m.hourlyRates?.length > 0) // only if hourly
					  .map(m => m.hourlyRates) // now an array of an array of hourly rates
					  .flat() // now an array of hourly rates
					  .filter(hr => hr.id === userId)  // only if this user is in the list!
					  .map(hr => hr.rate)
				  : []; // Now we can extract the rates

	// If no entries, we use the default rate. Otherwise we use the case-specified rate
	return rates.length > 0 ? Math.max(...rates) : (user?.billableRate ?? 0);
}

/**
 *
 * @param {Task[]} tasks
 * @param {boolean} newState
 */
export function onChangePrivacy(tasks: Task[], newState: boolean) {
    const skipped = tasks.filter(task => newState && !canMarkTaskPrivate(task,getCurrentUserId()));
    const updated = tasks.filter(task => !skipped.includes(task)).map(task => {
		let taskClone = task.clone();

		taskClone.private = newState;
		taskClone.addedBy = getCurrentUserId();
		taskClone.addedOn = newHRTimestamp();

		return taskClone;
	});

    if (updated.length) store.dispatch(TaskActions.saveManyTasksQueue(updated));
    return {updated,skipped};
}

/**
 * Move tasks to the selected date.
 *
 * @param {Task[]} tasks
 * @param {Date | Moment} dueDate
 * @param {SyntheticEvent} e
 * @param {boolean} [moveStart] - should we move the start time as well?
 * @returns {Task[]}
 */
export function onMoveDays(tasks: Task[], dueDate: Date | Moment.Moment, e?: SyntheticEvent, moveStart?: boolean): Task[] {
	e?.preventDefault?.();
	e?.stopPropagation?.();

	if (Moment.isMoment(dueDate)) dueDate = dueDate.toDate();

	const updated = tasks.map((t) => {
		const taskClone = t.clone();

		if (taskClone.dueDate !== dueDate) {
			taskClone.dueDate = dueDate;
			taskClone.dueDate = setHours(taskClone.dueDate, t.dueDate.getHours());
			taskClone.dueDate = setMinutes(taskClone.dueDate, t.dueDate.getMinutes());
			taskClone.dueDate = setSeconds(taskClone.dueDate, t.dueDate.getSeconds());

			if (moveStart) {
				taskClone.startTime = dueDate;
				// Only carry over the original time-of-day when a startTime exists;
				// a task with no startTime would otherwise crash on null.getHours().
				if (t.startTime) {
					taskClone.startTime = setHours(taskClone.startTime, t.startTime.getHours());
					taskClone.startTime = setMinutes(taskClone.startTime, t.startTime.getMinutes());
					taskClone.startTime = setSeconds(taskClone.startTime, t.startTime.getSeconds());
				}
			}

			taskClone.addedBy = getCurrentUserId();
			taskClone.addedOn = newHRTimestamp();
		}

		return taskClone;
	});

	store.dispatch(TaskActions.saveManyTasksQueue(updated));

	return updated;
}

/**
 * @param {MouseEvent} e
 * @param {Task} task
 */
export function onDelete(e: MouseEvent | null, task: Task) {
	e?.preventDefault?.();
	e?.stopPropagation?.();

	// OWA-930: a task delete also deletes its comments and documents, so it waits for the first fetch.
	if (!canCascadeDelete()) {
		reportDeleteResult('task', {status: 'notReady', impacts: []});
		return;
	}

	const taskClone = task.clone();

	// Y'all sure about that? A task that can not be deleted (it is on an invoice) is refused with the reason.
	confirmCascadeDelete({
		title: 'Delete Task',
		entityLabel: 'task',
		impacts: deleteImpactTask(taskClone),
		name: taskClone.title,
		onYes: (e?: SyntheticEvent) => {
			e?.preventDefault?.();
			e?.stopPropagation?.();
			reportDeleteResult('task', deleteTask(taskClone));
		},
		onNo: (e?: SyntheticEvent) => {
			e?.preventDefault?.();
			e?.stopPropagation?.();
		}
	});
}

/**
 * Duplicate the array of tasks.
 *
 * @param {Task[]} tasks
 * @returns {Task[]}
 */
export function onDuplicate(tasks: Task[]) {
	const updates = tasks.map(t => {
		const taskClone = t.duplicate('title');

		// Clear out invoice info
		// eslint-disable-next-line no-restricted-syntax -- brand-new duplicate task: detach from the source task's invoice. The clone has a fresh id, so no invoice back-points at it.
		taskClone.invoiceId = null;
		taskClone.elapsedTime = 0;
		taskClone.lastStart = 0;
		taskClone.timerStarted = false;
		taskClone.startTime = t.startTime;
		taskClone.dueDate = t.dueDate;
		taskClone.running = false;
		taskClone.completionDate = null;
		taskClone.cost = 0;
		taskClone.isCostOverridden = false;
		taskClone.costOverride = 0;
		taskClone.amountPaid = 0;
		taskClone.addedOn = newHRTimestamp();  // Have to override duplicate, as this is in a fast loop

        // Must match TaskItem's own key, or the reset clears nothing.
        const cache = componentCacheStore.typed<any>(`task-item-${taskClone.id}`);
        cache.resetItem();

		return taskClone;
	});

	store.dispatch(TaskActions.saveManyTasksQueue(updates));
}

/**
 *
 * @param {Task} task
 * @param {User|Group} userOrGroup
 * @param {Event} e
 */
export function onReassignTask(task: Task, userOrGroup: User | Group, e: MouseEvent | null) {
	e?.preventDefault?.();
	e?.stopPropagation?.();

	if (!guardTaskReassignment(task, message => Alert.addQuickFade(message))) return;

	const taskClone = task.clone();
	taskClone.assignedToType = userOrGroup instanceof User ? AssignedToType.User : AssignedToType.Group;
	taskClone.assignedTo = userOrGroup.id;
	taskClone.addedBy = getCurrentUserId();
	taskClone.addedOn = newHRTimestamp();

	if (!task.title) task.title = AWAITING_TITLE;
	store.dispatch(TaskActions.saveTaskQueue(taskClone));

}

/**
 * The User or Group this task is assigned to
 * @param {TaskLike} task
 * @return {User|Group}
 */
export function getAssigned(task: TaskLike): User | Group | null {
	if (!task.assignedTo) {
		const createdBy = store.getState().users.get(task.createdBy ?? '');
		if (!createdBy) {
			console.error('Task created by non-existent user', task);
			return null;
		}
		else return createdBy;
	}

	if (task.assignedToType === 'U') {
		const user = store.getState().users.get(task.assignedTo);
		if (user) return user;
		else {
			const group = store.getState().groups.get(task.assignedTo);
			if (!group) {
				console.error('Task does not seem to be assigned to a user or group', task.assignedTo);
				return null;
			}
			else {
				console.warn('Task assigned to group, not user', task.id);
				return group;
			}
		}
	}
	else if (task.assignedToType === 'G') {
		const group = store.getState().groups.get(task.assignedTo);
		if (group) return group;
		else {
			const user = store.getState().users.get(task.assignedTo);
			if (!user) {
				console.warn('Task does not seem to be assigned to a user or group');
				return null;
			}
			else {
				console.warn('Task assigned to user, not group', task.id);
				return user;
			}
		}
	}

	return null;
}

/**
 * Fetch the name of the group or user
 * @param {Group|User} groupOrUser
 * @param {object} [options]
 * @param {boolean} [options.unique]
 * @param {boolean} [options.shorten]
 * @param {boolean} [options.initials]
 * @returns {string}
 */
export function getAssignedName(groupOrUser: Group | User | null | undefined, options?: { unique?: boolean; shorten?: boolean; initials?: boolean; }): string {
	if (!groupOrUser) return '';

	if (groupOrUser instanceof User) {
		if (options?.initials) return getUserInitials(groupOrUser.id, 3);
		if (options?.unique) return formatUniqueName(getContactFromUser(groupOrUser) as Contact);
		else if (options?.shorten) return formatUserName(groupOrUser, {
			shorten: true,
			format: 'FML'
		});
		else return formatUserName(groupOrUser, {shorten: false, format: 'LFM'});
	}
	else if (groupOrUser instanceof Group) return groupOrUser.name;
	else {
		console.error('Not a group or user', groupOrUser);
		return 'Error!';
	}
}

/**
 * Get the name of the person assigned to this task (not their ID)
 *
 * @param {Task} task
 */
export function getAssignedFromTask(task: TaskLike): string {
	const userOrGroup = task.assignedToType === 'U'
						? store.getState().users.get(task.assignedTo ?? '')
						: store.getState().groups.get(task.assignedTo ?? '');

	return getAssignedName(userOrGroup, {initials: true})
}

/**
 * Is the assignedTo task equal to me, either as a user or a member of a group?
 *
 * @param {Task} task
 * @returns {boolean}
 */
export function amIAssigned(task: TaskLike): boolean {
	const assignedTo = task.assignedTo || task.createdBy;

	if (task.assignedToType === 'U') return assignedTo === getCurrentUserId();
	else {
		const myGroupIds = store.getState().groups
			.filter(g => g.userIds?.includes(getCurrentUserId()))
			.map(g => g.id);

		return myGroupIds.includes(assignedTo ?? '');
	}
}

/**
 * Filter out tasks to use for Associated Tasks
 *
 * @param {Task} task
 * @param {string} module
 * @param {string} entity
 * @param {string} entityId
 * @returns {boolean}
 */
export const taskFilter = (task: Task, module: string, entity: string, entityId: string): boolean => {
	return task.tieIns?.some(ti => ti.id === entityId && ti.module === module && ti.entity === entity) || false;
}

/**
 * Check if the task is related to the campaign.
 *
 * @param {Task} task
 * @param {string} campaignId
 * @returns {boolean}
 */
export function campaignFilter(task: Task, campaignId: string): boolean {
	return taskFilter(task, 'Marketing', 'Campaign', campaignId);
}

/**
 * Check if the task is related to the lead.
 *
 * @param {Task} task
 * @param {string} leadId
 * @returns {boolean}
 */
export function leadFilter(task: Task, leadId: string): boolean {
	return taskFilter(task, 'Sales', 'Lead', leadId);
}

/**
 * Check if the task is related to the matter.
 *
 * @param {Task} task
 * @param {string} matterId
 * @returns {boolean}
 */
export function matterFilter(task: Task, matterId: string): boolean {
	return taskFilter(task, 'Client', 'Matter', matterId);
}

/**
 * Check if the task is related to the contact.  This one is much more complex!
 *
 * @param {Task} task
 * @param {uuid} contactId
 * @returns {boolean}
 */
export const contactFilter = (task: Task, contactId: uuid): boolean => {
	// Check if this task includes the contactId in the participants field
	if (task.relatedTo.includes(contactId)) return true;

	// If this task isn't associated to a matter or lead, bail.
	if (!task.tieIns || task.tieIns.length === 0) return false;
	if (!task.tieIns.some(ti => ti.module === 'Client' || ti.module === 'Sales')) return false;

	// Check if this task is related to a lead or matter with this person as a client
	const matterIds = task.tieIns?.filter(ti => ti.module === 'Client').map(ti => ti.id) || [];

	const matterLeadIds = matterIds.length === 0 // Don't go through all matters if nothing to find
						  ? []
						  : store.getState()
							  .matters
							  .filter(matter => matterIds.includes(matter.id)) // Matters
							  .map(matter => matter.leadId); // leadIds

	const leadIds = task.tieIns?.filter(ti => ti.module === 'Sales').map(ti => ti.id) || [];

	if (matterLeadIds.length === 0 && leadIds.length === 0) return false; // Quick exit criteria

	const allLeadIds = Array.from(leadIds ? new Set([...matterLeadIds, ...leadIds]) : new Set(matterLeadIds));

	return !!allLeadIds.some(lid => !!store.getState().leads.get(lid ?? '')?.clients.includes(contactId));
};

/**
 * Get task-related comments for the given id.
 *
 * @param {uuid} id
 * @param {function} filter
 * @return {Task[]}
 */
export function getRelatedTasks(id: uuid, filter: FilterFnWithArgs<Task>): Task[] {
	return store.getState().tasks
		.filter((task) => filter(task, id));
}

export function getCampaignTasks(id: uuid) {
	return getRelatedTasks(id, campaignFilter);
}

export function getLeadTasks(id: uuid) {
	return getRelatedTasks(id, leadFilter);
}

export function getMatterTasks(id: uuid) {
	return getRelatedTasks(id, matterFilter);
}

export function getContactTasks(id: uuid) {
	return getRelatedTasks(id, contactFilter);
}

/**
 * @param {Task} task
 */
/**
 * Decide whether a task should appear in TaskGrid given the current
 * completed/incomplete toggles and lookback cutoff.
 *
 * - Incomplete tasks (no completionDate): governed by `showIncomplete`.
 * - Completed tasks with `showCompleted` off: only 'r'-status notifications
 *   addressed to the current user slip through.
 * - Completed tasks with `showCompleted` on: flagged tasks (notification
 *   status 'r' or 'w') bypass the lookback cutoff entirely (OWA-314 —
 *   flagged work always needs to be visible). Otherwise the task must have
 *   been completed after `lookbackCutoff`.
 *
 * Pure function so we can unit-test the matrix of conditions without
 * mounting the whole TaskGrid.
 */
export function shouldShowTaskForCompletion(
    task: Task,
    options: {
        lookbackCutoff: Date | null;
        showCompleted: boolean;
        showIncomplete: boolean;
        currentUserId: uuid;
    }
): boolean {
    const {lookbackCutoff, showCompleted, showIncomplete, currentUserId} = options;

    if (!task.completionDate) return showIncomplete;

    if (!showCompleted) {
        return task.notification?.status === 'r' && task.notification?.to === currentUserId;
    }

    const isFlagged = !!task.notification && ['r', 'w'].includes(task.notification.status);
    if (isFlagged) return true;

    if (lookbackCutoff === null) return true;
    return isAfter(task.completionDate, lookbackCutoff);
}

/**
 * Strip HTML from fields on a task that was just built from a TemplateTask.
 *
 * Older templates were edited with a rich-text editor that left HTML in
 * `title`. Newer templates store plain text. When an old template is
 * turned into a Task, those tags would surface as visible markup in the
 * task list (OWA-177). Strip them so the resulting task always has plain
 * text where the UI expects plain text. Mutates and returns the task for
 * chaining.
 */
export function stripTemplateHtml<T extends Task>(task: T): T {
	if (task.title) task.title = TextField.stripHTML(task.title, []);
	return task;
}

export function convertToTemplate(task: Task) {
	const taskClone = task.clone();

	const templateTask = new TemplateTask({
		firmId: getCurrentFirmId(),
		addedOn: newHRTimestamp(),
		addedBy: getCurrentUserId(),
		status: 'C',
		templateTaskId: util.genUUID(),
		title: taskClone.title,
		duration: taskClone.duration,
		urgency: taskClone.urgency,
		impact: taskClone.impact,
		categories: taskClone.categories,
		assignedToType: taskClone.assignedToType,
		assignedTo: taskClone.assignedTo,
		activityTypeIds: taskClone.activityTypeIds,
		billable: taskClone.billable,
		costOverride: taskClone.costOverride,
		isCostOverridden: taskClone.isCostOverridden,
		noCharge: taskClone.noCharge,
		hideFromInvoice: taskClone.hideFromInvoice,
		invoiceNotes: taskClone.invoiceNotes
	});

	store.dispatch(TemplateTaskActions.saveTemplateTaskQueue(templateTask));

	Alert.add(new Alert(Alert.SUCCESS, templateTask.id, templateTask.addedOn, 'Task converted to template'));
}

export type SaveTaskOptions = {
	editFirst?: boolean;
	dialogTitle?: string;
	isNew?: boolean;
	presets?: any;
	callback?: Function;
}

const SaveTaskOptionDefaults: SaveTaskOptions = {
	editFirst: false,
	dialogTitle: 'Create Task',
	isNew: false,
	presets: {},
	callback: undefined
}

/**
 *
 * @param {Task} task
 * @param {SaveTaskOptions} [options]
 */
export function saveTask(task: Task, options: SaveTaskOptions = SaveTaskOptionDefaults) {
	const {editFirst, dialogTitle, isNew, presets, callback} = options;

	if (editFirst) {
		const handleTask = (task: Task) => {
			if (!task.title) task.title = AWAITING_TITLE;
			store.dispatch(TaskActions.saveTaskQueue(task));
			if (callback && typeof (callback) === 'function') callback(task);
		};

		const cancelOut = () => {
		};

		dialogNoButtons(dialogTitle ?? 'Create Task',
			<TaskItem
				newId={isNew ? util.genUUID() : undefined}
				onOk={handleTask}
				onCancel={cancelOut}
				presets={presets}
				embedded={true}
				noCloseWarning={true}
			/>
		);
	}
	else {
		if (!task.title) task.title = AWAITING_TITLE;
		store.dispatch(TaskActions.saveTaskQueue(task));
	}
}

/**
 * Get the ActivityCategory related to this category.
 *
 * @param {string} categoryName
 * @returns {Category}
 */
export function getCategory(categoryName: string): Category {
	const primary = store.getState().categories.find(c => c.name === categoryName);
	if (primary) return primary;
	else return store.getState().categories
		.filter(c => c.relatedAreas.includes(categoryName))
		.sort((a, b) => a.addedOn - b.addedOn)[0];
}

/**
 * Get the ActivityCategory related to this category.
 *
 * @param {string} categoryName
 * @returns {ActivityCategory}
 */
export function getActivityCategory(categoryName: string): ActivityCategory {
	const primary = store.getState().activityCategories.find(ac => ac.name === categoryName);
	if (primary) return primary;
	else return store.getState().activityCategories
		.filter(ac => ac.relatedArea === categoryName)
		.sort((a, b) => a.addedOn - b.addedOn)[0];
}

/**
 * Get an ActivityType related to the category and activity name.
 *
 * @param {string} categoryName
 * @param {string} activityName
 * @return {ActivityCategory}
 */
export function getActivityType(categoryName: string, activityName: string): ActivityType | null {
	const categoryId = getActivityCategory(categoryName)?.id;
	if (!categoryId) {
		console.warn('Could not find category for', categoryName);
		return null;
	}

	return store.getState().activityTypes
		.find(at => at.activityCategoryId === categoryId && at.name === activityName) ?? null;
}

export function taskFailureReason(task: Task): null | {code: string, message: ReactNode} {
	// Check for tasks that are completed but still assigned to a group.
	const assignedTo = task.assignedTo || getCurrentUserId();
	if (task.assignedToType === AssignedToType.Group && task.completionDate) return {code: 'TS01', message: 'Task completed but assigned to group instead of user'};
	else if (task.assignedToType === AssignedToType.Group && !find<Group>('groups', task.assignedTo ?? ''))
		return {code: 'TS02', message: 'Task completed but assigned to a non-existent group'};
	else if (task.assignedToType === AssignedToType.User && !find<User>('users', assignedTo))
		return {code: 'TS03', message: 'Task completed but assigned to a non-existent user'};
	else return null;
}

/**
 *
 * @param {Task} task
 * @param {User[]} selectedUsers
 * @returns {boolean}
 */
export function userFilter(task: Task, selectedUsers: User[]): boolean {
	// Are we showing disabled users?
	const showDisabledUsers = locals.getBoolean(locals.TASK_SHOW_DISABLED_USERS, false);

	let users = selectedUsers ?? store.getState().users;
	users = users.filter(u => showDisabledUsers ? true : u.status !== 'D');

	// Either the task is assigned to the user, or the task was created by the user AND not reassigned.
	// Note: We use u.userId instead of u.id because selectedUsers may be plain objects from localStorage
	// that don't have the User class prototype (and thus don't have the id getter)
	const filteredByUser = users.filter(u => {
		// Get all groups the user belongs to
		const groupIds = store.getState().groups
			.filter(g => g.userIds?.includes(u.userId))
			.map(g => g.id);

		return (
			(!!task.assignedTo && (task.assignedTo === u.userId)) || // Task assigned to the user
			(task.assignedTo && groupIds.includes(task.assignedTo)) || // Task assigned to a group the member is a part of
			(task.relatedTo?.includes(u.contactId ?? '')) || // Include the user if the task includes them as a participant
			(task.addedBy === u.userId && ( // Task created by user AND
					task.assignedTo === null || // Task not reassigned OR
					(task.assignedTo && task.assignedTo === task.addedBy) // Task created by the user and assigned to themselves
				)
			)
		);
	});

	return filteredByUser.length > 0;
}

/**
 *
 * @param {Task} task
 * @param {string[]} selectedCategories
 * @returns {boolean}
 */
export function categoryFilter(task: Task, selectedCategories: uuid[]): boolean {
	// If no categories are selected, only return items with NO categorization
	if (selectedCategories.length === store.getState().categories.length) {
		// if all categories are selected, return everything
		return true;
	}
	else if (selectedCategories.length === 0) {
		return task.categories?.length === 0;
	}
	else {
		// If there is at least one category NOT selected, check for an intersection
		return selectedCategories.some(c => task.categories?.indexOf(c) !== -1);
	}
}

/**
 * Show dialog asking for new date.
 * @param {SyntheticEvent} e
 * @param {Task} task
 * @param {boolean} moveStart - should we move the start time as well?
 */
export function onMoveCustom(e: SyntheticEvent, task: Task, moveStart: boolean = false) {
	e.preventDefault();
	e.stopPropagation();

	const onChange = (date: Date | Date[], onClose?: () => void) => {
		onMoveDays([task], date as Date, e, moveStart);

		onClose?.();
	}

	dialogWithCancel('Pick a Date',
		<OutlawCalendar
			onChange={onChange}
			defaultValue={new Date()}
			isRange={false}
			dateFormat='dd'
			hideToday={true}
		/>,
		{maxWidth: '30em'}
	);
}

/**
 * Get actions for a task.
 *
 * @param {Task} task
 */
export function getContextMenuItems(task: Task): ContextMenuItemProps[] {
	const today = {
		label: 'Today',
		icon: Icons.CIRCLEDOT,
		color: 'darkorange',
		onClick: util.bouncify((e: SyntheticEvent) => onMoveDays([task], new Date(), e))
	};

	const tomorrow = {
		label: 'Next Day',
		icon: Icons.CIRCLERIGHT,
		color: 'darkorange',
		onClick: util.bouncify((e: SyntheticEvent) => onMoveDays([task], DateUtil.tomorrow(new Date()), e))
	};

	const nextWeek = {
		label: 'Next week',
		icon: Icons.CIRCLERIGHT,
		color: 'darkorange',
		onClick: (e: SyntheticEvent) => onMoveDays([task], DateUtil.nextWeek(new Date()), e)
	};

	const nextMonth = {
		label: 'Next month',
		icon: Icons.CIRCLERIGHT,
		color: 'darkorange',
		onClick: (e: SyntheticEvent) => onMoveDays([task], DateUtil.nextMonth(new Date()), e)
	};

	const customDate = {
		label: 'Pick a Date',
		icon: Icons.CALENDAR,
		color: 'darkorange',
		onClick: (e: SyntheticEvent) => onMoveCustom(e, task)
	};

	const moveTargets: ContextMenuItemProps[] = [];
	if (!isToday(task.dueDate)) moveTargets.push(today);
	if (!isTomorrow(task.dueDate)) moveTargets.push(tomorrow);
	if (!isNextWeek(task.dueDate)) moveTargets.push(nextWeek);
	if (!isNextUnit(task.dueDate, 'M')) moveTargets.push(nextMonth);
	moveTargets.push(customDate);

	const moveTo: ContextMenuItemProps = {
		label: canLinkTaskToFirmEntity(task) ? 'Move to' : 'Move to (make this task public first)',
        icon: Icons.CIRCLERIGHT, color: canLinkTaskToFirmEntity(task) ? 'darkorange' : 'gray', onClick: util.noop,
		subItems: canLinkTaskToFirmEntity(task) ? moveTargets : undefined
	};

	const assignTo: ContextMenuItemProps = {
		label: 'Assign to', icon: Icons.USER, color: '#cc9966', onClick: util.noop,
		subItems: store.getState().users
			.filter(u => u.enabled && (u.status !== 'D'))
			.filter(u => {
				// If assignedTo is null, show everybody except the current user
				if (task.assignedTo === null) return getCurrentUserId() !== u.id;
				// If assignedTo is set, allow anybody except the assignedTo user
				else return u.id !== task.assignedTo;
			})
			.map(u => {
				const result: ContextMenuItemProps = {
					label: formatUserName(u, {shorten: true, format: 'FML'}),
					icon: Icons.USER,
					color: '#cc9966',
					onClick: util.bouncify((e) => onReassignTask(task, u, e))
				};

				return result;
			})
	};

	const publicPrivate: ContextMenuItemProps = {
		label: `Mark ${task.private ? 'public' : 'private'}`,
		icon: Icons.PRIVATE,
		color: task.private ? 'black' : 'rgba(255,0,0,.6)',
		onClick: util.bouncify((e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			onChangePrivacy([task], !task.private)
		})
	};

	const duplicate: ContextMenuItemProps = {
		label: 'Duplicate',
		icon: Icons.CLONE,
		color: 'navy',
		onClick: util.bouncify((e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			onDuplicate([task])
		})
	};

	const items: ContextMenuItemProps[] = [
        task.private || canMarkTaskPrivate(task,getCurrentUserId()) ? publicPrivate : null,
		duplicate,
		moveTo
	].filter(util.existsFilter);

	if (store.getState().users.length > 1 && canReassignTasks(task)) items.push(assignTo);

	if (!task.invoiceId) items.push({
		label: 'Delete',
		icon: Icons.DELETE,
		// OWA-1779: offer Delete only when the delete is allowed, that is, when no impact is a blocker.
		showIf: (task: Task) => !deleteImpactTask(task).some(isBlocker),
		onClick: util.bouncify((e: MouseEvent) => onDelete(e, task)),
	});

	return items;
}
